import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createInventoryCount, type CreateInventoryCountPayload } from '../../api/stocuri'
import { listArticles } from '../../api/nomenclatoare'
import { listWarehouses, getWarehouse } from '../../api/depozite'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({ articleId: z.string().min(1, 'Selectați un articol'), zoneId: z.string().nullable(), locationId: z.string().nullable(), lotNumber: z.string().optional() })
const schema = z.object({
  countDate: z.string().min(1, 'Câmp obligatoriu'), warehouseId: z.string().min(1, 'Selectați un depozit'),
  notes: z.string().optional(), lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { countDate: new Date().toISOString().slice(0, 10), warehouseId: '', notes: '', lines: [] }

export default function CreateInventoryCountDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient()
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const warehouseId = useWatch({ control, name: 'warehouseId' })
  const { data: warehouseDetail } = useQuery({ queryKey: ['stocuri', 'depozit', warehouseId || null], queryFn: () => getWarehouse(warehouseId), enabled: !!warehouseId })

  useEffect(() => { if (open) reset(EMPTY) }, [open, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: CreateInventoryCountPayload = {
        countDate: values.countDate, warehouseId: values.warehouseId, notes: values.notes || null,
        lines: values.lines.map((l) => ({ articleId: l.articleId, zoneId: l.zoneId, locationId: l.locationId, lotNumber: l.lotNumber || null })),
      }
      return createInventoryCount(payload)
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['stocuri', 'inventar'] })
      onCreated(r.id)
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Numărătoare de inventar nouă</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="countDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data numărătorii" sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.countDate} helperText={errors.countDate?.message} />
              )} />
              <Controller name="warehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit" error={!!errors.warehouseId} helperText={errors.warehouseId?.message} />}
                />
              )} />
            </Stack>
            <Controller name="notes" control={control} render={({ field }) => (<TextField {...field} label="Note" fullWidth />)} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`lines.${i}.articleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.lines?.[i]?.articleId}
                      helperText={errors.lines?.[i]?.articleId?.message} />}
                  />
                )} />
                <Controller name={`lines.${i}.zoneId`} control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)} select label="Zonă" sx={{ width: 150 }}>
                    <MenuItem value="">—</MenuItem>
                    {warehouseDetail?.zones.map((z) => <MenuItem key={z.id} value={z.id!}>{z.name}</MenuItem>)}
                  </TextField>
                )} />
                <Controller name={`lines.${i}.lotNumber`} control={control} render={({ field }) => (
                  <TextField {...field} label="Lot (opțional)" sx={{ width: 150 }} />
                )} />
                <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Box>
              <Button size="small" startIcon={<Add />} onClick={() => lines.append({ articleId: '', zoneId: null, locationId: null, lotNumber: '' })}>
                Adaugă articol
              </Button>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Deschide numărătoarea
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
